/* -------------------------------------------------------------------------------------------------
 * ISOMORPHIC (should work in both client and server)
 * -----------------------------------------------------------------------------------------------*/

import {
  Block,
  CommonBlocksCompoundIndexes,
  isCommentBlock,
  isRichTextBlock,
  isRootBlock,
} from '@basehub/data/replicache/index'
import { TreeManager } from '@basehub/utils/tree/index'
import { TeamAllowances } from './allowances'
import { BlockUsage } from '@basehub/data/generic-redis/index'

/* -------------------------------------------------------------------------------------------------
 * Get block usage from tree manager
 * -----------------------------------------------------------------------------------------------*/

function shouldCountBlock(b: Block) {
  return !isRootBlock(b) && !isCommentBlock(b)
}

export const getBlockUsageFromTreeManager = (
  treeManager: TreeManager<Block, any>
) => {
  let blocks = 0

  for (const node of treeManager.getAllNodes()) {
    if (!shouldCountBlock(node.value)) continue
    if (node.parentKey) {
      const parentBlock = treeManager.getNode(node.parentKey)
      if (parentBlock) {
        const isChildOfRichText = isRichTextBlock(parentBlock.value)
        if (isChildOfRichText) continue
      }
    }

    blocks++
  }

  return blocks
}

/* -------------------------------------------------------------------------------------------------
 * Check usage allows commit
 * -----------------------------------------------------------------------------------------------*/

export type CheckUsageAllowsCommitParams = {
  stagingTreeManagerAboutToCommit: TreeManager<
    Block,
    Pick<CommonBlocksCompoundIndexes, 'blocksByType'>
  >
  currentTeamBlocksUsage: BlockUsage
  repoId: string
} & TeamAllowances['blocks']

export function checkUsageAllowsCommit({
  stagingTreeManagerAboutToCommit,
  currentTeamBlocksUsage,
  repoId,
  explicitLimit,
  maxPlanAllowance,
}: CheckUsageAllowsCommitParams):
  | { allowed: true; newBlocksUsage: BlockUsage }
  | { allowed: false; reason: string } {
  const currentUsageOfThisRepo =
    currentTeamBlocksUsage?.byRepoIds?.[repoId] ?? 0
  const newUsageFromThisRepo = getBlockUsageFromTreeManager(
    stagingTreeManagerAboutToCommit
  )

  const newTotalBlocksUsage =
    (currentTeamBlocksUsage?.total ?? 0) -
    // substract current usage from this repo, as we'll add it back with the working data
    currentUsageOfThisRepo +
    // add new one
    newUsageFromThisRepo

  // imagine a recently transferred repo, or a duplicated repo
  // info will appear post-commit
  const noInfoAboutThisRepo =
    currentTeamBlocksUsage?.byRepoIds?.[repoId] === undefined

  // imagine you somehow got to a higher than allowed blocks quantity, and now you wanna go back
  const isActuallyDeletingStuff =
    (currentTeamBlocksUsage?.total ?? 0) > newTotalBlocksUsage

  if (
    explicitLimit !== null &&
    newTotalBlocksUsage > explicitLimit &&
    !isActuallyDeletingStuff &&
    !noInfoAboutThisRepo
  ) {
    return {
      allowed: false,
      reason: `Block limit is set to ${explicitLimit} blocks committed. Intended to commit ${newTotalBlocksUsage} blocks.`,
    }
  }
  if (
    newTotalBlocksUsage > maxPlanAllowance &&
    !isActuallyDeletingStuff &&
    !noInfoAboutThisRepo
  ) {
    return {
      allowed: false,
      reason: `Your plan allows up to ${maxPlanAllowance} blocks committed. Intended to commit ${newTotalBlocksUsage} blocks.`,
    }
  }
  return {
    allowed: true,
    newBlocksUsage: {
      total: newTotalBlocksUsage,
      byRepoIds: {
        ...currentTeamBlocksUsage?.byRepoIds,
        [repoId]: newUsageFromThisRepo,
      },
    },
  }
}

export class CustomBlocksUsageError extends Error {
  reason: string

  constructor(reason: string) {
    super('Block usage error')
    this.reason = reason
  }
}
